/**
 * DebugMode 组件
 * 用于显示 ProcessStep 的原始数据，主要用于调试和开发
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep } from '../../../../../types';

interface DebugModeProps {
  step: ProcessStep;
  stepIndex?: number;
}

export const DebugMode: React.FC<DebugModeProps> = ({ step, stepIndex }) => {
  const { t } = useTranslation(['v3/chat']);

  // 格式化任意数据为JSON字符串
  const formatAsJson = (data: any): string => {
    try {
      if (typeof data === 'string') return data;
      if (typeof data === 'object' && data !== null && data.error && data.raw) {
        return t('v3/chat:processSteps.errorParsing', { error: data.error, raw: data.raw });
      }
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  // 获取步骤图标
  const getStepIcon = () => {
    switch (step.type) {
      case 'text':
        return '💬';
      case 'tool_result':
        return '🔧';
      case 'tool_error':
        return '❌';
      default:
        return '⚙️';
    }
  };

  // 获取步骤标题
  const getStepTitle = () => {
    switch (step.type) {
      case 'text':
        return t('v3/chat:processSteps.textStep');
      case 'tool_result':
        return t('v3/chat:processSteps.toolResult', { name: step.toolName });
      case 'tool_error':
        return t('v3/chat:processSteps.toolError');
      default:
        return t('v3/chat:processSteps.unknownStep');
    }
  };

  return (
    <div className="border border-ios-gray5 dark:border-ios-dark-gray4 rounded-lg p-3 bg-white dark:bg-ios-dark-gray6">
      {/* Step 头部 */}
      <div className="flex items-start gap-2 mb-2">
        <div className="mt-0.5 text-gray-600 dark:text-gray-400">
          {getStepIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-medium text-ios-black dark:text-white">
              {getStepTitle()}
            </h4>
            <span className="text-xs text-ios-gray3 dark:text-ios-dark-gray3">
              #{step.sequence || stepIndex || 1}
            </span>
          </div>
          <div className="text-xs text-ios-gray3 dark:text-ios-dark-gray3">
            {new Date(step.timestamp).toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Step 内容 - JSON 格式展示 */}
      <div className="mt-2">
        <div className="text-xs text-ios-gray3 dark:text-ios-dark-gray3 mb-1">
          {t('v3/chat:processSteps.stepData')}:
        </div>
        <pre className="text-xs bg-ios-gray7 dark:bg-ios-dark-black p-2 rounded overflow-x-auto max-h-40 overflow-y-auto font-mono">
          {formatAsJson(step)}
        </pre>
      </div>

      {/* 特殊处理：tool_result 和 tool_error 显示状态 */}
      {(step.type === 'tool_result' || step.type === 'tool_error') && (
        <div className="mt-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ios-gray3 dark:text-ios-dark-gray3">
              {t('v3/chat:processSteps.status.label')}:
            </span>
            <span className={`text-xs px-2 py-1 rounded-full ${
              step.status === 'success'
                ? 'bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100'
                : 'bg-red-100 text-red-700 dark:bg-red-700 dark:text-red-100'
            }`}>
              {step.status === 'success' ? t('v3/chat:processSteps.status.success') : step.status}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugMode;
